import { Icons, Modal } from '@pankod/refine-antd';
import React, { ReactNode, useCallback, useState } from 'react';
import styles from './mobile-action-confirm-modal.module.css';

type ConfirmConfig = {
  title?: ReactNode;
  okText?: string;
  cancelText?: string;
};

type ActionLike = {
  label?: ReactNode;
  danger?: boolean;
  confirm?: ConfirmConfig;
};

type Props = {
  open: boolean;
  itemName?: ReactNode;
  sku?: string;
  action?: ActionLike | null;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
};

export const MobileActionConfirmModal: React.FC<Props> = ({
  open,
  itemName,
  sku,
  action,
  onConfirm,
  onCancel,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const itemNameText = typeof itemName === 'string' ? itemName : 'this item';
  const actionLabelText =
    typeof action?.label === 'string' ? action.label : action?.danger ? 'Delete' : 'Confirm';
  const isDeleteAction =
    Boolean(action?.danger) || actionLabelText.toLowerCase().includes('delete');

  const title =
    action?.confirm?.title ??
    (isDeleteAction ? `Delete “${itemNameText}”?` : `Confirm “${actionLabelText}”?`);

  const okText = action?.confirm?.okText ?? (isDeleteAction ? 'Delete' : 'Confirm');
  const cancelText = action?.confirm?.cancelText ?? 'Cancel';

  const handleOk = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
      onCancel();
    } catch {
      // keep modal open
    } finally {
      setIsSubmitting(false);
    }
  }, [onCancel, onConfirm]);

  const handleCancel = useCallback(() => {
    setIsSubmitting(false);
    onCancel();
  }, [onCancel]);

  return (
    <Modal
      visible={open}
      centered
      closable={!isSubmitting}
      maskClosable={false}
      destroyOnClose
      wrapClassName={styles.modal}
      title={
        <span className={styles.title}>
          <span className={styles.titleIcon} aria-hidden="true">
            <Icons.ExclamationCircleOutlined />
          </span>
          <span className={styles.titleText}>{title}</span>
        </span>
      }
      okText={okText}
      cancelText={cancelText}
      confirmLoading={isSubmitting}
      cancelButtonProps={{ disabled: isSubmitting }}
      okButtonProps={{ danger: Boolean(action?.danger) }}
      onOk={handleOk}
      onCancel={handleCancel}
      keyboard={!isSubmitting}
      afterClose={() => setIsSubmitting(false)}
    >
      <div className={styles.content}>
        <div className={styles.message}>
          {isDeleteAction ? (
            <>
              This will permanently delete <strong>{itemNameText}</strong>.
            </>
          ) : (
            <>
              You’re about to <strong>{actionLabelText}</strong> <strong>{itemNameText}</strong>.
            </>
          )}
        </div>
        {sku ? (
          <div className={styles.meta}>
            <span className={styles.metaLabel}>SKU:</span>{' '}
            <span className={styles.metaValue}>{sku}</span>
          </div>
        ) : null}
        {/* {isDeleteAction ? <div className={styles.hint}>This action can’t be undone.</div> : null} */}
      </div>
    </Modal>
  );
};
